import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Building2, Search, Plus, X, Edit2, Trash2, Eye, Phone, Mail,
  MapPin, Calendar, DollarSign, Shield, Filter, CheckCircle, AlertCircle, Clock
} from 'lucide-react'
import './ClientArea.css'

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

const statusColors = {
  Active: '#4caf50', Inactive: '#9e9e9e', Pending: '#ff9800', Suspended: '#f44336'
}

const paymentStatusColors = {
  Paid: '#4caf50', Pending: '#ff9800', Overdue: '#f44336'
}

const ClientArea = () => {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [editingClient, setEditingClient] = useState(null)
  const [viewingClient, setViewingClient] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [formData, setFormData] = useState({
    companyName: '', contactPerson: '', email: '', phone: '', address: '', city: '',
    country: 'Kenya', industry: '', contractStart: '', contractEnd: '', status: 'Active',
         guardsAssigned: 0, billingCycle: 'Monthly', ratePerGuard: '', paymentStatus: 'Pending', notes: '', referredBy: '', referralType: '', commission: '', commissionStatus: 'Pending', referralDate: ''
  })

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 4000)
  }

  const fetchClients = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const params = {}
      if (statusFilter) params.status = statusFilter
      if (searchTerm) params.search = searchTerm
      const res = await axios.get(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` }, params
      })
      setClients(res.data.data || [])
    } catch (err) {
      showMessage('error', 'Failed to load clients')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchClients() }, [statusFilter])

  const resetForm = () => {
    setFormData({
      companyName: '', contactPerson: '', email: '', phone: '', address: '', city: '',
      country: 'Kenya', industry: '', contractStart: '', contractEnd: '', status: 'Active',
           guardsAssigned: 0, billingCycle: 'Monthly', ratePerGuard: '', paymentStatus: 'Pending', notes: '',
      referredBy: '', referralType: '', commission: '', commissionStatus: 'Pending', referralDate: ''
    })
    setEditingClient(null)
  }

  const openCreateModal = () => { resetForm(); setShowModal(true) }

  const openEditModal = (client) => {
    setFormData({
      companyName: client.companyName || '', contactPerson: client.contactPerson || '',
      email: client.email || '', phone: client.phone || '', address: client.address || '',
      city: client.city || '', country: client.country || 'Kenya', industry: client.industry || '',
      contractStart: client.contractStart ? client.contractStart.split('T')[0] : '',
      contractEnd: client.contractEnd ? client.contractEnd.split('T')[0] : '',
      status: client.status || 'Active', guardsAssigned: client.guardsAssigned || 0,
      billingCycle: client.billingCycle || 'Monthly', ratePerGuard: client.ratePerGuard || '',
               paymentStatus: client.paymentStatus || 'Pending', notes: client.notes || '',
        referredBy: client.referredBy || '', referralType: client.referralType || '',
        commission: client.commission || '', commissionStatus: client.commissionStatus || 'Pending',
        referralDate: client.referralDate ? client.referralDate.split('T')[0] : ''
    })
    setEditingClient(client)
    setShowModal(true)
  }

  const openViewModal = (client) => { setViewingClient(client); setShowViewModal(true) }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('token')
      const payload = { ...formData }
      if (editingClient) {
        await axios.put(`${API_URL}/clients/${editingClient._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        })
        showMessage('success', 'Client updated successfully')
      } else {
        await axios.post(`${API_URL}/clients`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        })
        showMessage('success', 'Client created successfully')
      }
      setShowModal(false)
      resetForm()
      fetchClients()
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Operation failed')
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this client?')) return
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showMessage('success', 'Client deleted successfully')
      fetchClients()
    } catch (err) {
      showMessage('error', 'Failed to delete client')
    }
  }

  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      (client.companyName || '').toLowerCase().includes(term) ||
      (client.contactPerson || '').toLowerCase().includes(term) ||
      (client.email || '').toLowerCase().includes(term)
    )
  })

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'Active').length,
    pending: clients.filter(c => c.status === 'Pending').length,
    overdue: clients.filter(c => c.paymentStatus === 'Overdue').length
  }

  return (
    <div className="client-area">
      <div className="ca-header">
        <div className="ca-header-left">
          <Building2 className="ca-icon" />
          <div>
            <h1 className="ca-title">Client Area</h1>
            <p className="ca-subtitle">Manage clients, contracts & billing</p>
          </div>
        </div>
        <button className="ca-btn-primary" onClick={openCreateModal}>
          <Plus size={18} /> Add Client
        </button>
      </div>

      <div className="ca-stats">
        <div className="ca-stat-card">
          <div className="ca-stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
            <Building2 size={20} />
          </div>
          <div className="ca-stat-info">
            <span className="ca-stat-value">{stats.total}</span>
            <span className="ca-stat-label">Total Clients</span>
          </div>
        </div>
        <div className="ca-stat-card">
          <div className="ca-stat-icon" style={{ background: '#e8f5e9', color: '#388e3c' }}>
            <CheckCircle size={20} />
          </div>
          <div className="ca-stat-info">
            <span className="ca-stat-value">{stats.active}</span>
            <span className="ca-stat-label">Active</span>
          </div>
        </div>
        <div className="ca-stat-card">
          <div className="ca-stat-icon" style={{ background: '#fff3e0', color: '#f57c00' }}>
            <Clock size={20} />
          </div>
          <div className="ca-stat-info">
            <span className="ca-stat-value">{stats.pending}</span>
            <span className="ca-stat-label">Pending</span>
          </div>
        </div>
        <div className="ca-stat-card">
          <div className="ca-stat-icon" style={{ background: '#ffebee', color: '#d32f2f' }}>
            <AlertCircle size={20} />
          </div>
          <div className="ca-stat-info">
            <span className="ca-stat-value">{stats.overdue}</span>
            <span className="ca-stat-label">Payment Overdue</span>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`ca-message ca-message-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      <div className="ca-filters">
        <div className="ca-search-box">
          <Search size={18} className="ca-search-icon" />
          <input type="text" placeholder="Search clients..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchClients()} />
          <button className="ca-search-btn" onClick={fetchClients}>Search</button>
        </div>
        <div className="ca-filter-group">
          <Filter size={16} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Pending">Pending</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>
      </div>

      <div className="ca-table-container">
        {loading ? (
          <div className="ca-loading">
            <div className="ca-spinner"></div>
            <p>Loading clients...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="ca-empty">
            <Building2 size={48} className="ca-empty-icon" />
            <h3>No clients found</h3>
            <p>Add your first client to get started</p>
            <button className="ca-btn-primary" onClick={openCreateModal}>
              <Plus size={18} /> Add Client
            </button>
          </div>
        ) : (
          <table className="ca-table">
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Company</th>
                <th>Contact</th>
                <th>Phone / Email</th>
                <th>Guards</th>
                <th>Status</th>
                <th>Referral</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map(client => (
                <tr key={client._id}>
                  <td><span className="ca-client-id">{client.clientId || '-'}</span></td>
                  <td>
                    <div className="ca-company">
                      <span className="ca-company-name">{client.companyName}</span>
                      <span className="ca-company-city">{client.city || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{client.contactPerson}</td>
                  <td>
                    <div className="ca-contact">
                      <span><Phone size={14} /> {client.phone}</span>
                      <span><Mail size={14} /> {client.email}</span>
                    </div>
                  </td>
                  <td>
                    <span className="ca-guards-badge">
                      <Shield size={14} /> {client.guardsAssigned || 0}
                    </span>
                  </td>
                                      <td>
                      {client.referredBy ? (
                        <div className="ca-referral">
                          <span className="ca-referrer">{client.referredBy}</span>
                          {client.commission > 0 && (
                            <span className="ca-commission" style={{
                              background: client.commissionStatus === 'Paid' ? '#e8f5e9' : '#fff3e0',
                              color: client.commissionStatus === 'Paid' ? '#2e7d32' : '#ef6c00',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              marginTop: '4px',
                              display: 'inline-block'
                            }}>
                              {client.commissionStatus === 'Paid' ? '✓ ' : '⏳ '}
                              KES {client.commission?.toLocaleString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{color: '#999', fontSize: '12px'}}>-</span>
                      )}
                    </td>
                  <td>
                    <span className="ca-status-badge" style={{
                      background: statusColors[client.status] + '20',
                      color: statusColors[client.status]
                    }}>{client.status}</span>
                  </td>
                  <td>
                    <span className="ca-payment-badge" style={{
                      color: paymentStatusColors[client.paymentStatus]
                    }}>
                      <DollarSign size={12} /> {client.paymentStatus}
                    </span>
                  </td>
                  <td>
                    <div className="ca-actions">
                      <button className="ca-action-btn ca-view" onClick={() => openViewModal(client)}><Eye size={16} /></button>
                      <button className="ca-action-btn ca-edit" onClick={() => openEditModal(client)}><Edit2 size={16} /></button>
                      <button className="ca-action-btn ca-delete" onClick={() => handleDelete(client._id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="ca-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h2>{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="ca-modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="ca-form">
              <div className="ca-form-grid">
                <div className="ca-form-group">
                  <label>Company Name *</label>
                  <input type="text" required value={formData.companyName}
                    onChange={e => setFormData({...formData, companyName: e.target.value})} placeholder="Enter company name" />
                </div>
                <div className="ca-form-group">
                  <label>Contact Person *</label>
                  <input type="text" required value={formData.contactPerson}
                    onChange={e => setFormData({...formData, contactPerson: e.target.value})} placeholder="Enter contact person" />
                </div>
                <div className="ca-form-group">
                  <label>Email *</label>
                  <input type="email" required value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})} placeholder="email@company.com" />
                </div>
                <div className="ca-form-group">
                  <label>Phone *</label>
                  <input type="tel" required value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+254 700 000 000" />
                </div>
                <div className="ca-form-group ca-form-full">
                  <label>Address</label>
                  <input type="text" value={formData.address}
                    onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Street address" />
                </div>
                <div className="ca-form-group">
                  <label>City</label>
                  <input type="text" value={formData.city}
                    onChange={e => setFormData({...formData, city: e.target.value})} placeholder="City" />
                </div>
                <div className="ca-form-group">
                  <label>Country</label>
                  <input type="text" value={formData.country}
                    onChange={e => setFormData({...formData, country: e.target.value})} placeholder="Country" />
                </div>
                <div className="ca-form-group">
                  <label>Industry</label>
                  <input type="text" value={formData.industry}
                    onChange={e => setFormData({...formData, industry: e.target.value})} placeholder="e.g. Manufacturing" />
                </div>
                <div className="ca-form-group">
                  <label>Contract Start</label>
                  <input type="date" value={formData.contractStart}
                    onChange={e => setFormData({...formData, contractStart: e.target.value})} />
                </div>
                <div className="ca-form-group">
                  <label>Contract End</label>
                  <input type="date" value={formData.contractEnd}
                    onChange={e => setFormData({...formData, contractEnd: e.target.value})} />
                </div>
                <div className="ca-form-group">
                  <label>Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
                <div className="ca-form-group">
                  <label>Guards Assigned</label>
                  <input type="number" min="0" value={formData.guardsAssigned}
                    onChange={e => setFormData({...formData, guardsAssigned: parseInt(e.target.value) || 0})} />
                </div>
                <div className="ca-form-group">
                  <label>Billing Cycle</label>
                  <select value={formData.billingCycle} onChange={e => setFormData({...formData, billingCycle: e.target.value})}>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annually">Annually</option>
                  </select>
                </div>
                <div className="ca-form-group">
                  <label>Rate Per Guard (KES)</label>
                  <input type="number" min="0" value={formData.ratePerGuard}
                    onChange={e => setFormData({...formData, ratePerGuard: e.target.value})} placeholder="0.00" />
                </div>
                <div className="ca-form-group">
                  <label>Payment Status</label>
                  <select value={formData.paymentStatus} onChange={e => setFormData({...formData, paymentStatus: e.target.value})}>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
                <div className="ca-form-group ca-form-full">
                  <label>Notes</label>
                  <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes..." rows={3} />
                  <div className="ca-form-group ca-form-full">
                    <h4 style={{margin: '16px 0 8px', color: '#1a237e', borderBottom: '2px solid #e0e0e0', paddingBottom: '8px'}}>
                      👤 Referral Information
                    </h4>
                  </div>
                  <div className="ca-form-group">
                    <label>Referred By</label>
                    <input type="text" value={formData.referredBy}
                      onChange={e => setFormData({...formData, referredBy: e.target.value})} placeholder="Name of referrer" />
                  </div>
                  <div className="ca-form-group">
                    <label>Referral Type</label>
                    <select value={formData.referralType}
                      onChange={e => setFormData({...formData, referralType: e.target.value})}>
                      <option value="">Select Type</option>
                      <option value="Employee">Employee</option>
                      <option value="Partner">Partner</option>
                      <option value="Client">Client</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="ca-form-group">
                    <label>Commission (KES)</label>
                    <input type="number" min="0" value={formData.commission}
                      onChange={e => setFormData({...formData, commission: e.target.value})} placeholder="0.00" />
                  </div>
                  <div className="ca-form-group">
                    <label>Commission Status</label>
                    <select value={formData.commissionStatus}
                      onChange={e => setFormData({...formData, commissionStatus: e.target.value})}>
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                      <option value="Waived">Waived</option>
                    </select>
                  </div>
                  <div className="ca-form-group">
                    <label>Referral Date</label>
                    <input type="date" value={formData.referralDate}
                      onChange={e => setFormData({...formData, referralDate: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="ca-form-actions">
                <button type="button" className="ca-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="ca-btn-primary">
                  {editingClient ? 'Update Client' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && viewingClient && (
        <div className="ca-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="ca-modal ca-modal-view" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h2>Client Details</h2>
              <button className="ca-modal-close" onClick={() => setShowViewModal(false)}><X size={20} /></button>
            </div>
            <div className="ca-view-content">
              <div className="ca-view-header">
                <div className="ca-view-company">
                  <Building2 size={32} />
                  <div>
                    <h3>{viewingClient.companyName}</h3>
                    <span className="ca-view-id">{viewingClient.clientId}</span>
                  </div>
                </div>
                <span className="ca-status-badge" style={{
                  background: statusColors[viewingClient.status] + '20',
                  color: statusColors[viewingClient.status]
                }}>{viewingClient.status}</span>
              </div>
              <div className="ca-view-grid">
                <div className="ca-view-section">
                  <h4>Contact Information</h4>
                  <p><strong>Person:</strong> {viewingClient.contactPerson}</p>
                  <p><strong>Phone:</strong> {viewingClient.phone}</p>
                  <p><strong>Email:</strong> {viewingClient.email}</p>
                  <p><strong>Address:</strong> {viewingClient.address || 'N/A'}, {viewingClient.city || 'N/A'}</p>
                </div>
                <div className="ca-view-section">
                  <h4>Contract Details</h4>
                  <p><strong>Start:</strong> {viewingClient.contractStart ? new Date(viewingClient.contractStart).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>End:</strong> {viewingClient.contractEnd ? new Date(viewingClient.contractEnd).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Industry:</strong> {viewingClient.industry || 'N/A'}</p>
                  <p><strong>Billing:</strong> {viewingClient.billingCycle}</p>
                </div>
                <div className="ca-view-section">
                  <h4>Financial</h4>
                  <p><strong>Rate/Guard:</strong> KES {viewingClient.ratePerGuard || 0}</p>
                  <p><strong>Payment:</strong> <span style={{ color: paymentStatusColors[viewingClient.paymentStatus] }}>{viewingClient.paymentStatus}</span></p>
                </div>
                <div className="ca-view-section">
                  <h4>Operations</h4>
                  <p><strong>Guards Assigned:</strong> {viewingClient.guardsAssigned || 0}</p>
                  <p><strong>Created:</strong> {viewingClient.createdAt ? new Date(viewingClient.createdAt).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              {viewingClient.notes && (
                <div className="ca-view-notes">
                  <h4>Notes</h4>
                  <p>{viewingClient.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientArea
